import { RenderItemProps, VirtualList } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import type { Order } from '../../models/Orders';
import { Card } from './Card';
import { Checkbox } from '@shared/components/ui/checkbox';

// 🎯 Interface Segregation Principle (ISP) - Mismas props que LocationTable
interface CardListProps {
  orders: Order[];
  isLoading?: boolean;
  className?: string;
  // 🎯 Props para scroll infinito
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting - manejado desde el padre
  sorting?: any;
  onSortingChange?: (sorting: any) => void;
  total?: number;
  // 🎯 Props específicas para cards
  showActions?: boolean;
  // 🎯 Prop para estimación de altura cuando es dinámica
  estimateItemHeight?: number;
  // 🎯 Props de selección
  selectedOrders?: Set<number>;
  onToggleOrderSelection?: (order: Order) => void;
  onSelectAllOrders?: () => void;
  // 🎯 Prop para ocultar columnas seleccionadas
  selectedColumns?: string[];
}

export const CardList = ({
  orders,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  total,
  className = '',
  showActions = true,
  estimateItemHeight = 120, // 🎯 Estimación conservadora para cards con productos
  selectedOrders,
  onToggleOrderSelection,
  onSelectAllOrders,
  selectedColumns,
}: CardListProps) => {
  const { t } = useTranslation('ordersModule');

  const areAllSelected = orders.length > 0 && selectedOrders?.size === orders.length;

  // 🎯 Render function que usa el componente LocationCard
  const renderLocationCard = (props: RenderItemProps<Order>) => (
    <Card
      {...props}
      showActions={showActions}
      isSelected={selectedOrders?.has(props.item.id) || false}
      onToggleSelect={() => onToggleOrderSelection?.(props.item)}
      selectedColumns={selectedColumns}
    />
  );

  // 🎯 Key generator para optimizar renders
  const getOrdersKey = (orders: Order) => {
    return orders.id;
  };

  return (
    <div>
      <div className="flex items-center gap-2 border-b border-card-border pb-4 pt-2">
        <Checkbox checked={areAllSelected} onCheckedChange={onSelectAllOrders} className="w-4 h-4" />
        <span className="text-sm font-medium">{t('selection.selectAll', 'Seleccionar todos')}</span>
      </div>
      <VirtualList
        data={orders}
        renderItem={renderLocationCard}
        itemKey={getOrdersKey}
        // 🎯 Configuración optimizada para móvil con altura dinámica
        estimateItemHeight={estimateItemHeight} // 🎯 Solo estimación, altura real será medida
        height="calc(100vh - 150px)" // Altura completa en móvil
        gap={0} // Sin gap entre cards
        emptyMessage={t('empty.message')}
        className={`rounded-lg shadow-sm ${className}`}
        // 🎯 Props de scroll infinito
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        infiniteScrollThreshold={300} // Threshold mayor para móvil
        // 🎯 Props de sorting (aunque en móvil se use menos)
        sorting={sorting}
        onSortingChange={onSortingChange}
        total={total}
        // 🎯 Styling específico para móvil
        listClassName="touch-pan-y" // Mejor scroll en mobile
        itemClassName="touch-manipulation" // Optimización touch
      />
    </div>
  );
};
